import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { backButtonCallback } from '@/router'
import { useConfig } from '@/composables/game/config'
import { usePlayer } from '@/composables/game/player'
import { useNow } from '@/composables/game/time'
import { REWARD_LIVE_OP_CHALLENGE_TYPE, type RewardLiveOp } from '@/types/shared/player-config'
import type { Step, StepReward } from '@/types/shared/pathway-event'

const showPuzzles = ref(false)
const selectedPuzzleId = ref<number | null>(null)

export type PuzzlePiece = {
  stepKey: string
  stepNumber: number
  step: Step
  completed: boolean
}

const PUZZLE_PIECE_LIMIT = 12

export const usePuzzles = (options?: { watch?: boolean }) => {
  const { config, syncConfig } = useConfig()
  const { player, updatePlayer } = usePlayer()
  const { now } = useNow()

  const allPuzzleLiveOps = computed((): RewardLiveOp[] => {
    const rewardLiveOps = config.value.game.rewardLiveOps
    if (rewardLiveOps.length === 0) {
      return []
    }

    return rewardLiveOps
      .filter(op => {
        if (op.challenge !== REWARD_LIVE_OP_CHALLENGE_TYPE.PUZZLE) {
          return false
        }

        return op.start <= now.value && op.end >= now.value
      })
      .sort((a, b) => a.instanceDuration - b.instanceDuration)
  })

  const unclaimedPuzzleLiveOps = computed((): RewardLiveOp[] => {
    return allPuzzleLiveOps.value.filter(op => {
      const progress = player.value.rewardLiveOps.runningEvents[op.id]
      return !progress?.claimed
    })
  })

  const firstExistingPuzzleLiveOp = computed((): RewardLiveOp | null => {
    return allPuzzleLiveOps.value[0] || null
  })

  const firstPuzzleLiveOp = computed((): RewardLiveOp | null => {
    return unclaimedPuzzleLiveOps.value[0] || null
  })

  if (options?.watch) {
    const globalCountdownForEventEnd = computed(() => {
      if (allPuzzleLiveOps.value.length === 0) return 0

      const countdowns = allPuzzleLiveOps.value.map(puzzleLiveOp => {
        const playerProgress = player.value.rewardLiveOps.runningEvents[puzzleLiveOp.id]

        if (playerProgress?.startTimestamp) {
          const startInSeconds = Math.floor(playerProgress.startTimestamp / 1000)
          const eventEndTime = startInSeconds + playerProgress.duration
          return Math.max(eventEndTime - now.value, 0)
        }

        const eventStart = puzzleLiveOp.start
        const instanceDuration = puzzleLiveOp.instanceDuration
        const timeSinceStart = now.value - eventStart
        const timeInCurrentInstance = timeSinceStart % instanceDuration
        const timeUntilInstanceEnd = instanceDuration - timeInCurrentInstance

        return Math.max(timeUntilInstanceEnd, 0)
      })

      return Math.min(...countdowns)
    })

    watch(globalCountdownForEventEnd, async countdown => {
      if (countdown === 0) {
        setTimeout(async () => {
          const { playerStats } = await syncConfig()
          updatePlayer(playerStats)
        }, 1000)
      }
    })
  }

  return {
    allPuzzleLiveOps,
    unclaimedPuzzleLiveOps,
    firstExistingPuzzleLiveOp,
    firstPuzzleLiveOp,
  }
}

export const usePuzzle = (puzzleLiveOpRef: Ref<RewardLiveOp | null> | ComputedRef<RewardLiveOp | null> | RewardLiveOp) => {
  const { player } = usePlayer()
  const { now } = useNow()

  const puzzleLiveOp = computed(() => {
    if ('value' in puzzleLiveOpRef) {
      return puzzleLiveOpRef.value
    }
    return puzzleLiveOpRef as RewardLiveOp
  })

  const playerProgress = computed(() => {
    if (!puzzleLiveOp.value) return null
    return player.value.rewardLiveOps.runningEvents[puzzleLiveOp.value.id] ?? null
  })

  const countdownForEventEnd = computed(() => {
    if (!puzzleLiveOp.value) return 0

    if (playerProgress.value?.startTimestamp) {
      const startInSeconds = Math.floor(playerProgress.value.startTimestamp / 1000)
      const eventEndTime = startInSeconds + playerProgress.value.duration
      return Math.max(eventEndTime - now.value, 0)
    }

    const eventStart = puzzleLiveOp.value.start
    const instanceDuration = puzzleLiveOp.value.instanceDuration
    const timeSinceStart = now.value - eventStart
    const timeInCurrentInstance = timeSinceStart % instanceDuration
    const timeUntilInstanceEnd = instanceDuration - timeInCurrentInstance

    return Math.max(timeUntilInstanceEnd, 0)
  })

  const puzzleGrid = computed((): PuzzlePiece[] => {
    if (!puzzleLiveOp.value) return []

    const completedSteps = playerProgress.value?.completedSteps ?? []

    return Object.entries(puzzleLiveOp.value.config.steps).map(([stepKey, step], index) => {
      const stepNumber = index + 1

      return {
        stepKey,
        stepNumber,
        step,
        completed: completedSteps.includes(stepNumber),
      }
    }).slice(0, PUZZLE_PIECE_LIMIT)
  })

  const completedPuzzles = computed(() =>
    puzzleGrid.value.filter(puzzle => puzzle.completed).length,
  )

  const totalPuzzles = computed(() => puzzleGrid.value.length)

  const totalRewards = computed<Partial<StepReward>>(() => {
    if (!puzzleLiveOp.value) {
      return {
        goldenCoins: 0,
        spins: 0,
        giveawayTickets: 0,
        dropeeStars: 0,
        specialCurrencies: {},
      }
    }

    const configReward = puzzleLiveOp.value.config.reward
    return {
      goldenCoins: configReward.goldenCoins || 0,
      spins: configReward.spins || 0,
      giveawayTickets: configReward.giveawayTickets || 0,
      dropeeStars: configReward.dropeeStars || 0,
      specialCurrencies: configReward.specialCurrencies,
    }
  })

  const puzzleBackgroundImage = computed(() => {
    return puzzleLiveOp.value?.image || null
  })

  const isPuzzleRewardClaimed = computed(() => {
    return playerProgress.value?.claimed
  })

  const showPuzzle = computed(() => {
    return !!puzzleLiveOp.value && !isPuzzleRewardClaimed.value
  })

  return {
    playerProgress,
    puzzleGrid,
    puzzleBackgroundImage,
    completedPuzzles,
    totalPuzzles,
    totalRewards,
    countdownForEventEnd,
    isPuzzleRewardClaimed,
    showPuzzle,
  }
}

export const usePuzzlesOverlay = () => {
  const showPuzzlesOverlay = computed(() => showPuzzles.value)

  function hidePuzzlesOverlay() {
    backButtonCallback.value = null
    showPuzzles.value = false
    selectedPuzzleId.value = null
  }

  function openPuzzlesOverlay(puzzleId?: number) {
    backButtonCallback.value = hidePuzzlesOverlay
    showPuzzles.value = true
    selectedPuzzleId.value = puzzleId ?? null
  }

  return {
    showPuzzlesOverlay,
    hidePuzzlesOverlay,
    openPuzzlesOverlay,
    selectedPuzzleId: computed(() => selectedPuzzleId.value),
  }
}